'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Button } from '../../../../../components/styled/button';
import { Text } from '../../../../../components/styled/text';
import { Textarea } from '../../../../../components/styled/textarea';
import { Input } from '../../../../../components/styled/input';
import { IconButton } from '../../../../../components/icon-button';
import { Field } from '../../../../../components/field';
import {
  Select,
  createSelectListCollection,
} from '../../../../../components/select';
import { Badge } from '../../../../../components/badge';
import { Switch } from '../../../../../components/switch';
import * as Slider from '../../../../../components/styled/slider';
import {
  Alert01Icon,
  CheckmarkCircle02Icon,
  Copy01Icon,
  Undo02Icon,
  FloppyDiskIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { MarkdownPreview } from '../../../../markdown-preview';
import { Tooltip } from '../../../../../components/tooltip';
import {
  AGENTIC_AI_SIGNAL_CATEGORIES,
  defaultStageForSignalStatus,
  getSignalCategoriesForTargetType,
  isSignalCategoryAllowedForTargetType,
  SignalStatusSchema,
  type SignalCategoryType,
  type SignalStatusType,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import type { SignalData } from '../types';

export type EnhancedEditSignalFormProps = {
  onClose: () => void;
  onSubmit: (editedSignals: SignalData[]) => void;
  signals: SignalData[];
  actionName?: string;
  headerActions?: React.ReactNode;
  allowSubmitWithoutChanges?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  targetType?: TargetTypeType;
};
type FieldValidation = {
  isValid: boolean;
  message?: string;
};
const AGENTIC_CATEGORY_SET = new Set<string>(AGENTIC_AI_SIGNAL_CATEGORIES);

function resolveHitlCategoryTargetType({
  signals,
  targetType,
}: {
  signals: SignalData[];
  targetType?: TargetTypeType;
}): TargetTypeType {
  if (targetType) {
    return targetType;
  }

  for (const signal of signals) {
    const category = signal.category;

    if (!category) {
      continue;
    }

    if (AGENTIC_CATEGORY_SET.has(category)) {
      return 'AI_APPLICATION';
    }

    if (
      isSignalCategoryAllowedForTargetType(category, 'INTERNAL_NETWORK') &&
      !isSignalCategoryAllowedForTargetType(category, 'WEB_APPLICATION')
    ) {
      return 'INTERNAL_NETWORK';
    }

    if (
      isSignalCategoryAllowedForTargetType(category, 'EXTERNAL_NETWORK') &&
      !isSignalCategoryAllowedForTargetType(category, 'WEB_APPLICATION')
    ) {
      return 'EXTERNAL_NETWORK';
    }
  }

  return 'WEB_APPLICATION';
}

const ORDERED_SIGNAL_STATUS: readonly SignalStatusType[] = [
  SignalStatusSchema.enum.DISPROVEN_VULNERABILITY,
  SignalStatusSchema.enum.POTENTIAL_VULNERABILITY,
  SignalStatusSchema.enum.CONFIRMED_VULNERABILITY,
  SignalStatusSchema.enum.BLOCKED,
  SignalStatusSchema.enum.DUPLICATE,
];

export function EnhancedEditSignalForm({
  onClose,
  onSubmit,
  signals,
  actionName,
  headerActions,
  allowSubmitWithoutChanges,
  onApprove,
  onReject,
  targetType,
}: EnhancedEditSignalFormProps) {
  const t = useTranslations('designSystem.chat.editSignalModal');
  const tCommon = useTranslations('designSystem.common');
  const tSignalStatus = useTranslations(
    'designSystem.signals.lifecycle.status',
  );
  const [editedSignals, setEditedSignals] = useState<SignalData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalSignals, setOriginalSignals] = useState<SignalData[]>([]);
  const statusCollection = useMemo(
    () =>
      createSelectListCollection({
        items: ORDERED_SIGNAL_STATUS.map((status: SignalStatusType) => ({
          label: tSignalStatus(status),
          value: status,
        })),
      }),
    [tSignalStatus],
  );
  const categoryCollection = useMemo(() => {
    const resolvedTargetType = resolveHitlCategoryTargetType({
      signals: editedSignals.length > 0 ? editedSignals : signals,
      targetType,
    });
    const categories = [
      ...getSignalCategoriesForTargetType(resolvedTargetType),
    ];

    for (const signal of editedSignals.length > 0 ? editedSignals : signals) {
      if (signal.category && !categories.includes(signal.category)) {
        categories.push(signal.category);
      }
    }

    return createSelectListCollection({
      items: categories.map((category: SignalCategoryType) => ({
        label: formatCategoryForDisplay(category),
        value: category,
      })),
    });
  }, [editedSignals, signals, targetType]);

  useEffect(() => {
    if (signals.length > 0) {
      const signalsCopy = JSON.parse(JSON.stringify(signals));

      setEditedSignals(signalsCopy);
      setOriginalSignals(signalsCopy);
      setCurrentIndex(0);
      setHasUnsavedChanges(false);
    }
  }, [signals]);
  const currentSignal = editedSignals[currentIndex];
  const originalSignal = originalSignals[currentIndex];
  const validateField = useCallback(
    (
      field: keyof SignalData,
      value: string | number | boolean | undefined,
    ): FieldValidation => {
      if (field === 'title' && (!value || String(value).trim().length === 0)) {
        return { isValid: false, message: t('validation.titleRequired') };
      }

      if (
        field === 'description' &&
        (!value || String(value).trim().length < 10)
      ) {
        return {
          isValid: false,
          message: t('validation.descriptionMinLength'),
        };
      }

      if (field === 'category' && !value) {
        return { isValid: false, message: t('validation.categoryRequired') };
      }

      return { isValid: true };
    },
    [t],
  );
  const isCurrentSignalValid = useMemo(() => {
    if (!currentSignal) return false;
    const titleValid = validateField('title', currentSignal.title).isValid;
    const descriptionValid = validateField(
      'description',
      currentSignal.description,
    ).isValid;
    const categoryValid = validateField(
      'category',
      currentSignal.category,
    ).isValid;

    return titleValid && descriptionValid && categoryValid;
  }, [currentSignal, validateField]);
  const isFieldModified = useCallback(
    (field: keyof SignalData): boolean => {
      return originalSignal?.[field] !== currentSignal?.[field];
    },
    [originalSignal, currentSignal],
  );
  const handleFieldChange = useCallback(
    (field: keyof SignalData, value: SignalData[keyof SignalData]) => {
      setEditedSignals((prev) => {
        const updated = [...prev];

        updated[currentIndex] = {
          ...updated[currentIndex],
          [field]: value,
        };

        return updated;
      });
      setHasUnsavedChanges(true);
    },
    [currentIndex],
  );
  const handleStatusChange = useCallback(
    (status: SignalStatusType | undefined) => {
      setEditedSignals((prev) => {
        const updated = [...prev];

        updated[currentIndex] = {
          ...updated[currentIndex],
          status,
          ...(status !== undefined && {
            stage: defaultStageForSignalStatus({ status }),
          }),
        };

        return updated;
      });
      setHasUnsavedChanges(true);
    },
    [currentIndex],
  );
  const handleResetField = useCallback(
    (field: keyof SignalData) => {
      if (originalSignal) {
        handleFieldChange(field, originalSignal[field]);
      }
    },
    [originalSignal, handleFieldChange],
  );
  const handleCopyField = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
  }, []);
  const handleSubmit = useCallback(() => {
    if (!hasUnsavedChanges && allowSubmitWithoutChanges && onApprove) {
      onApprove();

      return;
    }

    onSubmit(editedSignals);
    onClose();
  }, [
    allowSubmitWithoutChanges,
    editedSignals,
    hasUnsavedChanges,
    onApprove,
    onSubmit,
    onClose,
  ]);
  const handleClose = useCallback(() => {
    setEditedSignals([]);
    setCurrentIndex(0);
    setHasUnsavedChanges(false);
    onClose();
  }, [onClose]);
  const getSubmitButtonText = useMemo(() => {
    if (!actionName) return t('submit');
    const lowerName = actionName.toLowerCase();

    if (lowerName.includes('create') || lowerName.includes('signal-tool')) {
      return t('createSignal');
    }

    if (lowerName.includes('update')) {
      return t('updateSignal');
    }

    return t('submit');
  }, [actionName, t]);
  const renderFieldWithPreview = useCallback(
    ({
      field,
      label,
      value,
      placeholder,
      isLarge,
    }: {
      field: keyof SignalData;
      label: string;
      value: string;
      placeholder: string;
      isLarge?: boolean;
    }) => {
      const validation = validateField(field, value);
      const isModified = isFieldModified(field);

      return (
        <Field.Root>
          <HStack justifyContent="space-between" alignItems="center" mb={1}>
            <HStack gap={2} alignItems="center">
              <Field.Label fontSize="xs" fontWeight="medium">
                {label}
              </Field.Label>
              {isModified && (
                <Badge variant="emphasis" size="sm">
                  {t('modified')}
                </Badge>
              )}
              {!validation.isValid && (
                <Tooltip title={validation.message}>
                  <Alert01Icon size={14} color="var(--colors-fg-error)" />
                </Tooltip>
              )}
            </HStack>
            <HStack gap={1}>
              {value && (
                <Tooltip title={t('copy')}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => handleCopyField(value)}
                    aria-label={t('copyField')}
                  >
                    <Copy01Icon size={14} />
                  </IconButton>
                </Tooltip>
              )}
              {isModified && (
                <Tooltip title={t('resetToOriginal')}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => handleResetField(field)}
                    aria-label={t('resetField')}
                  >
                    <Undo02Icon size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </HStack>
          </HStack>

          {isLarge ? (
            <Textarea
              value={value || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              placeholder={placeholder}
              rows={5}
              resize="vertical"
              size="sm"
            />
          ) : (
            <Input
              size="sm"
              value={value || ''}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              placeholder={placeholder}
            />
          )}
        </Field.Root>
      );
    },
    [
      validateField,
      isFieldModified,
      handleFieldChange,
      handleCopyField,
      handleResetField,
      t,
    ],
  );

  if (!currentSignal) {
    return null;
  }

  const modifiedFieldsCount = Object.keys(currentSignal).filter((key) =>
    isFieldModified(key as keyof SignalData),
  ).length;

  return (
    <VStack h="full" w="full" overflow="hidden">
      <HStack w="full" justifyContent="space-between" alignItems="center" p={4}>
        <VStack alignItems="flex-start" gap={0}>
          <Text fontSize="md" fontWeight="semibold">
            {t('title')}
          </Text>
          <Text fontSize="xs" color="fg.muted">
            {t('description')}
          </Text>
        </VStack>
        <HStack gap={1}>
          {headerActions}
          <IconButton
            variant="ghost"
            size="xs"
            onClick={handleClose}
            aria-label={t('close')}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>
      </HStack>

      <VStack
        flex={1}
        overflow="auto"
        px={4}
        w="full"
        gap={4}
        alignItems="stretch"
      >
        <VStack
          gap={3}
          p={4}
          bg="bg.default"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.default"
          w="full"
          alignItems="stretch"
        >
          <HStack w="full" justifyContent="space-between" alignItems="center">
            <Text fontSize="sm" fontWeight="semibold">
              {t('tabs.details')}
            </Text>
          </HStack>

          {renderFieldWithPreview({
            field: 'title',
            label: t('fields.title'),
            value: currentSignal.title || '',
            placeholder: t('placeholders.title'),
          })}

          <HStack gap={3} width="100%" alignItems="flex-start">
            <Field.Root flex={1}>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('fields.category')}
              </Field.Label>
              <Select.Root
                positioning={{
                  sameWidth: true,
                  strategy: 'fixed',
                }}
                size="sm"
                collection={categoryCollection}
                value={currentSignal.category ? [currentSignal.category] : []}
                onValueChange={(e) =>
                  handleFieldChange('category', e.value[0] || '')
                }
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t('placeholders.category')}
                    />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup>
                      {categoryCollection.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Field.Root>

            <Field.Root flex={1}>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('fields.label')}
              </Field.Label>
              <Select.Root
                positioning={{
                  sameWidth: true,
                  strategy: 'fixed',
                }}
                size="sm"
                collection={statusCollection}
                value={currentSignal.status ? [currentSignal.status] : []}
                onValueChange={(e) =>
                  handleStatusChange(e.value[0] as SignalStatusType | undefined)
                }
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('placeholders.label')} />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup>
                      {statusCollection.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Field.Root>
          </HStack>

          <Field.Root>
            <HStack justifyContent="space-between" alignItems="center" mb={1}>
              <HStack gap={2} alignItems="center">
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('fields.confidence')}
                </Field.Label>
                {isFieldModified('confidence') && (
                  <Badge variant="emphasis" size="sm">
                    {t('modified')}
                  </Badge>
                )}
              </HStack>
              <Badge
                variant="emphasis"
                size="sm"
                colorScheme={
                  (currentSignal.confidence ?? 3) >= 4
                    ? 'green'
                    : (currentSignal.confidence ?? 3) >= 3
                      ? 'yellow'
                      : 'orange'
                }
              >
                {currentSignal.confidence
                  ? `${Math.round((currentSignal.confidence / 5) * 100)}%`
                  : '60%'}
              </Badge>
            </HStack>
            <Slider.Root
              min={20}
              max={100}
              step={1}
              value={[
                currentSignal.confidence
                  ? Math.max(20, Math.min(100, currentSignal.confidence * 20))
                  : 60,
              ]}
              onValueChange={(e) =>
                handleFieldChange(
                  'confidence',
                  Math.max(1, Math.min(5, Math.round(e.value[0] / 20))),
                )
              }
            >
              <Slider.Control>
                <Slider.Track>
                  <Slider.Range />
                </Slider.Track>
                <Slider.Thumb index={0} />
              </Slider.Control>
            </Slider.Root>
          </Field.Root>
        </VStack>

        <VStack
          gap={3}
          p={4}
          bg="bg.default"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.default"
          w="full"
          alignItems="stretch"
        >
          <HStack w="full" justifyContent="space-between" alignItems="center">
            <Text fontSize="sm" fontWeight="semibold">
              {t('tabs.hypothesis')}
            </Text>
          </HStack>

          {renderFieldWithPreview({
            field: 'hypothesis',
            label: t('fields.hypothesis'),
            value: currentSignal.hypothesis || '',
            placeholder: t('placeholders.hypothesis'),
            isLarge: true,
          })}

          {renderFieldWithPreview({
            field: 'description',
            label: t('fields.description'),
            value: currentSignal.description || '',
            placeholder: t('placeholders.description'),
            isLarge: true,
          })}

          {renderFieldWithPreview({
            field: 'analysis',
            label: t('fields.analysis'),
            value: currentSignal.analysis || '',
            placeholder: t('placeholders.analysis'),
            isLarge: true,
          })}
        </VStack>

        <VStack
          gap={3}
          p={4}
          bg="bg.default"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.default"
          w="full"
          alignItems="stretch"
        >
          <HStack w="full" justifyContent="space-between" alignItems="center">
            <Text fontSize="sm" fontWeight="semibold">
              {t('tabs.context')}
            </Text>
          </HStack>

          {renderFieldWithPreview({
            field: 'reason',
            label: t('fields.reason'),
            value: currentSignal.reason || '',
            placeholder: t('placeholders.reason'),
            isLarge: true,
          })}

          <Field.Root>
            <HStack
              justifyContent="space-between"
              alignItems="center"
              p={3}
              bg="bg.subtle"
              borderRadius="md"
            >
              <VStack alignItems="flex-start" gap={0.5}>
                <Text fontSize="xs" fontWeight="medium">
                  {t('fields.isAuthenticated')}
                </Text>
                <Text fontSize="2xs" color="fg.muted">
                  {t('fields.isAuthenticatedDescription')}
                </Text>
              </VStack>
              <HStack gap={2} alignItems="center">
                {isFieldModified('isAuthenticated') && (
                  <Badge variant="emphasis" size="sm">
                    {t('modified')}
                  </Badge>
                )}
                <Switch
                  checked={currentSignal.isAuthenticated ?? false}
                  onCheckedChange={(details: { checked: boolean }) =>
                    handleFieldChange('isAuthenticated', details.checked)
                  }
                  size="sm"
                />
              </HStack>
            </HStack>
          </Field.Root>

          {(currentSignal.endpointId ||
            currentSignal.harEntryId ||
            currentSignal.entryPointId ||
            currentSignal.signalId) && (
            <VStack alignItems="start" gap={2} width="100%">
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted">
                {t('fields.linkedResources')}
              </Text>
              <Box p={3} bg="bg.subtle" borderRadius="md" width="100%">
                <VStack alignItems="start" gap={1.5}>
                  {currentSignal.signalId && (
                    <HStack gap={2}>
                      <Text fontSize="xs" color="fg.muted">
                        {t('fields.signalId')}:
                      </Text>
                      <Text fontSize="xs" fontFamily="mono">
                        {currentSignal.signalId}
                      </Text>
                    </HStack>
                  )}
                  {currentSignal.endpointId && (
                    <HStack gap={2}>
                      <Text fontSize="xs" color="fg.muted">
                        {t('fields.endpointId')}:
                      </Text>
                      <Text fontSize="xs" fontFamily="mono">
                        {currentSignal.endpointId}
                      </Text>
                    </HStack>
                  )}
                  {currentSignal.harEntryId && (
                    <HStack gap={2}>
                      <Text fontSize="xs" color="fg.muted">
                        {t('fields.harEntryId')}:
                      </Text>
                      <Text fontSize="xs" fontFamily="mono">
                        {currentSignal.harEntryId}
                      </Text>
                    </HStack>
                  )}
                  {currentSignal.entryPointId && (
                    <HStack gap={2}>
                      <Text fontSize="xs" color="fg.muted">
                        {t('fields.entryPointId')}:
                      </Text>
                      <Text fontSize="xs" fontFamily="mono">
                        {currentSignal.entryPointId}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            </VStack>
          )}
        </VStack>

        {currentSignal.securityTestCaseSummary && (
          <VStack
            gap={3}
            p={4}
            bg="bg.default"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            w="full"
            alignItems="stretch"
          >
            <HStack w="full" justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="semibold">
                {t('fields.securityTestCase')}
              </Text>
              <Tooltip title={t('removeTestCase')}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  colorPalette="red"
                  onClick={() =>
                    handleFieldChange('securityTestCaseSummary', '')
                  }
                  aria-label={t('removeTestCase')}
                >
                  <Cancel01Icon size={14} />
                </IconButton>
              </Tooltip>
            </HStack>
            <Box
              p={3}
              bg="bg.subtle"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.default"
              overflow="auto"
            >
              <MarkdownPreview
                source={currentSignal.securityTestCaseSummary}
                showCodeLineNumbers
              />
            </Box>
          </VStack>
        )}
      </VStack>

      <HStack
        gap={2}
        justifyContent="space-between"
        width="100%"
        p={4}
        flexShrink={0}
        borderTopWidth="1px"
        borderTopColor="border.default"
        bg="bg.subtle"
      >
        <Text fontSize="2xs" color="fg.muted">
          {hasUnsavedChanges
            ? t('fieldsModifiedCount', {
                count: modifiedFieldsCount,
              })
            : t('noChanges')}
        </Text>
        <HStack gap={2}>
          <Button
            variant="outline"
            size="sm"
            colorPalette={onReject ? 'red' : undefined}
            onClick={() => {
              if (onReject) {
                onReject();

                return;
              }

              handleClose();
            }}
          >
            {onReject ? tCommon('reject') : t('cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={
              (!allowSubmitWithoutChanges && !hasUnsavedChanges) ||
              !isCurrentSignalValid
            }
          >
            {allowSubmitWithoutChanges ? (
              <CheckmarkCircle02Icon size={14} />
            ) : (
              <FloppyDiskIcon size={14} />
            )}
            {getSubmitButtonText}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
}
